/**
 * Phone numbers as a shopkeeper types them, turned into what WhatsApp needs.
 *
 * People write a number the way they say it - 0300-1234567, 0300 123 4567,
 * +92 300 1234567 - and WhatsApp wants 923001234567: digits only, country code
 * included, no plus, no leading zero. Nobody should have to know that.
 *
 * Pakistan is assumed for a local-looking number because that is the shop whole
 * customer list, but a number that already carries a country code is left
 * alone, so an overseas customer still works.
 */

const PK = '92'

const digits = (value: string): string => value.replace(/\D+/g, '')

/**
 * International digits ready for a wa.me link, or null when the number is too
 * short to be real. Null is the honest answer for a half-typed number - the
 * WhatsApp button simply stays hidden rather than opening a broken chat.
 */
export function waNumber(raw: string | null | undefined): string | null {
  if (!raw) return null
  const hadPlus = raw.trim().startsWith('+')
  const d = digits(raw)
  if (d.length < 7) return null

  // Already carries a country code: trust it and leave it be.
  if (hadPlus) return d
  if (d.startsWith(PK) && d.length === 12) return d

  // 0300 1234567 - the way it is written on every card in Karachi.
  if (d.startsWith('0')) return PK + d.slice(1)

  // 3001234567 - the leading zero dropped somewhere along the way.
  if (d.length === 10 && d.startsWith('3')) return PK + d

  // Anything else is passed through rather than mangled into a wrong number.
  return d
}

export const canWhatsApp = (raw: string | null | undefined): boolean => waNumber(raw) !== null

/**
 * Back to the local form for display: 923001234567 becomes 0300-1234567.
 * Seeing it the familiar way is how a typo gets noticed.
 */
export function prettyPhone(raw: string | null | undefined): string {
  const wa = waNumber(raw)
  if (!wa) return raw?.trim() ?? ''
  if (wa.startsWith(PK) && wa.length === 12) {
    const local = `0${wa.slice(2)}`
    return `${local.slice(0, 4)}-${local.slice(4)}`
  }
  return `+${wa}`
}

/**
 * Opens WhatsApp at this number with the message ready to send.
 *
 * wa.me carries text only - WhatsApp own limit, not a choice made here. Sending
 * the bill picture itself goes through the share sheet instead.
 */
export function whatsAppLink(raw: string | null | undefined, message: string): string | null {
  const wa = waNumber(raw)
  if (!wa) return null
  return `https://wa.me/${wa}?text=${encodeURIComponent(message)}`
}

/**
 * WhatsApp with the message ready but no contact chosen.
 *
 * The fallback for somebody whose number is not in the book, which is most of
 * them until the day they are all typed in. Without this the WhatsApp button
 * simply vanished for those customers, and the shop was left with no way to
 * send a bill at all - which is worse than asking WhatsApp to pick the chat.
 */
export function whatsAppComposeLink(message: string): string {
  return `https://wa.me/?text=${encodeURIComponent(message)}`
}
